import { ConversationMetadata, UserMessage, SystemInitiationMessage } from "@stratify/shared";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { store } from "../../../store/store";
import { trackEvent } from "../../analytics/store/analyticsSlice";
import { AgentConfig, AgentContext } from "../types/agentConfig";
import { agentConfigRegistry } from "./agentConfigRegistry";

/**
 * Generic service for managing agent conversations
 */
export class GenericAgentConversationService {
  private config: AgentConfig;

  constructor(agentType: AgentConfig['agentType']) {
    this.config = agentConfigRegistry.getConfig(agentType);
  }

  /**
   * Get the conversations collection path for the given context
   */
  getConversationsPath(context: AgentContext): string {
    return this.config.getConversationsPath(context.contextId, context.userId);
  }

  /**
   * Get the messages collection path for the given context and conversation
   */
  getMessagesPath(context: AgentContext, conversationId: string): string {
    return this.config.getMessagesPath(context.contextId, conversationId, context.userId);
  }

  /**
   * Create a new conversation
   */
  async createConversation(
    context: AgentContext, 
    title: string
  ): Promise<string> {
    // Track conversation creation
    store.dispatch(
      trackEvent({ 
        name: `${this.config.analyticsPrefix}_conversation_created`,
        params: { agentType: this.config.agentType }
      })
    );

    const metadata: ConversationMetadata = {
      title: title.slice(0, 30) + (title.length > 30 ? "..." : ""),
      isLoading: true,
      isWorking: true,
      createdAt: serverTimestamp(),
    };

    const conversationRef = await addDoc(
      collection(db, this.getConversationsPath(context)),
      metadata
    );

    return conversationRef.id;
  }

  /**
   * Add a message to a conversation
   */
  async addMessage(
    context: AgentContext,
    conversationId: string,
    text: string
  ): Promise<void> {
    // Track message sent
    store.dispatch(
      trackEvent({
        name: `${this.config.analyticsPrefix}_message_sent`,
        params: { 
          messageLength: text.length,
          agentType: this.config.agentType 
        },
      })
    );

    const message: UserMessage = {
      type: "user",
      content: {
        text,
      },
      timestamp: serverTimestamp(),
    };

    await addDoc(
      collection(db, this.getMessagesPath(context, conversationId)),
      message
    );
  }

  /**
   * Add a system initiation message to a conversation
   */
  async addSystemInitiationMessage(
    context: AgentContext,
    conversationId: string,
    text: string
  ): Promise<void> {
    // Track system initiation
    store.dispatch(
      trackEvent({
        name: `${this.config.analyticsPrefix}_system_initiation`,
        params: { 
          messageLength: text.length,
          agentType: this.config.agentType 
        },
      })
    );

    const message: SystemInitiationMessage = {
      type: "system_initiation",
      content: {
        text,
      },
      timestamp: serverTimestamp(),
    };

    await addDoc(
      collection(db, this.getMessagesPath(context, conversationId)),
      message
    );
  }

  /**
   * Get agent configuration
   */
  getConfig(): AgentConfig {
    return this.config;
  }
}